import { defineMessages } from 'react-intl';

export const Actions = Object.freeze({
  AUTO_PLAY_VIDEO_BEGIN: 'AUTO_PLAY_VIDEO_BEGIN',
  AUTO_PLAY_VIDEO_END: 'AUTO_PLAY_VIDEO_END',
  FLEX_VIDEO_BEGIN: 'FLEX_PLAY_VIDEO_BEGIN',
  FLEX_VIDEO_END: 'FLEX_PLAY_VIDEO_END',
  USER_INITIATED_VIDEO_BEGIN: 'USER_INITIATED_VIDEO_BEGIN',
  USER_INITIATED_VIDEO_END: 'USER_INITIATED_VIDEO_END',

  SET_VIDEO_AD_COMPANION: 'VIDEO_AD_SET_COMPANION',
  SET_VIDEO_AD_SHOWING: 'VIDEO_AD_SET_SHOWING',
  SET_VIDEO_AD_INITIAL: 'VIDEO_AD_SET_INITIAL',
  SET_VIDEO_ANALYTICS_CONTEXT: 'VIDEO_AD_SET_ANALYTICS_CONTEXT',
  SET_VIDEO_AD_FLEX_PROPERTIES: 'VIDEO_AD_SET_FLEX_PROPERTIES',

  FLEX_VIDEO_ATTEMPT_DISMISS: 'FLEX_VIDEO_ATTEMPT_DISMISS',
  FLEX_VIDEO_HAS_DISMISSED: 'FLEX_VIDEO_HAS_DISMISSED',
  FLEX_VIDEO_RESUMED_FROM_MODAL: 'FLEX_VIDEO_RESUMED_FROM_MODAL',
  FLEX_VIDEO_OPENED_RESUME_MODAL: 'FLEX_VIDEO_OPENED_RESUME_MODAL',

  SHOW_COMPANION_AD: 'SHOW_COMPANION_AD',
  INCREMENT_VIDEO_INDEX: 'VIDEO_AD_INCREMENT_VIDEO_INDEX',
  SET_VIDEO_AD_THRESHOLD_MET: 'VIDEO_AD_SET_THRESHOLD_MET',

  LOG_IAS_IMA_ADAPTER_LOAD_FAIL: 'LOG_IAS_IMA_ADAPTER_LOAD_FAIL',

  // control actions
  PLAY: 'VIDEO_AD_PLAYER_PLAY',
  PAUSE: 'VIDEO_AD_PLAYER_PAUSE',
  SET_VOLUME: 'VIDEO_AD_PLAYER_SET_VOLUME',
  MUTE: 'VIDEO_AD_PLAYER_MUTE',
  UNMUTE: 'VIDEO_AD_PLAYER_UNMUTE',
  CLOSE: 'VIDEO_AD_PLAYER_CLOSE',
  LEARN_MORE: 'VIDEO_AD_PLAYER_LEARN_MORE',
  FORCE_CLOSE: 'VIDEO_AD_PLAYER_FORCE_CLOSE',
  SEEK_BEGIN: 'VIDEO_AD_PLAYER_SEEK_BEGIN',
  SEEK_END: 'VIDEO_AD_PLAYER_SEEK_END',
  EMIT_EVENT: 'VIDEO_AD_PLAYER_EMIT_EVENT',
});

export const Events = Object.freeze({
  PLAYER_RENDERED: 'VIDEO_AD_PLAYER_RENDERED',

  // player events, used by the IMA SDK wrapper to map events to those interpreted by the app
  LOADING: 'VIDEO_AD_PLAYER_EVENT_LOADING',
  LOADED: 'VIDEO_AD_PLAYER_EVENT_LOADED',
  PLAYING: 'VIDEO_AD_PLAYER_EVENT_PLAYING',
  PAUSED: 'VIDEO_AD_PLAYER_EVENT_PAUSED',
  ERROR: 'VIDEO_AD_PLAYER_EVENT_ERROR',
  VOLUME_CHANGE: 'VIDEO_AD_PLAYER_EVENT_VOLUME_CHANGE',
  MUTE: 'VIDEO_AD_PLAYER_EVENT_MUTED',
  UNMUTE: 'VIDEO_AD_PLAYER_EVENT_UNMUTE',
  DONE: 'VIDEO_AD_PLAYER_EVENT_DONE',
  CURRENT_TIME: 'VIDEO_AD_PLAYER_EVENT_CURRENT_TIME',
  RESIZE: 'VIDEO_AD_PLAYER_EVENT_RESIZE',
  COMPANION: 'VIDEO_AD_PLAYER_EVENT_COMPANION',
  TRACKING: 'VIDEO_AD_PLAYER_EVENT_TRACKING',
  STARTED: 'VIDEO_AD_PLAYER_EVENT_STARTED',
  CONTEXT: 'VIDEO_AD_PLAYER_EVENT_CONTEXT',
  EMPTY_AD: 'VIDEO_AD_PLAYER_EVENT_EMPTY_AD',
  COMPLETE: 'VIDEO_AD_PLAYER_EVENT_COMPLETE',
  CLICKED: 'TAP_TO_VIDEO_CLICKED',
});

export const PlaybackTypes = Object.freeze({
  AutoPlayVideo: 'AutoPlayVideo',
  Flex: 'Flex',
  UserInitiatedVideo: 'UserInitiatedVideo',
});

// SOURCE: https://jira.savagebeast.com/browse/WEBCLIENT-3532?focusedCommentId=4313762&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-4313762
/*
  requirement: WEBCA-1303 - Match Haymaker ad title pattern for ad suppression

  HOUSE: /#HAYMAKER_EMPTY_TOKEN#/i
    case-insensitive match for the above string
  FLEX & SUPPRESSOR: /HM_PARSE_\d+,"?\w+"?,"?\w+"?,EMPTY/i
    case-insensitive match
    \d+ for different numeric versions
    "?\w+"? for any alphanumeric strings with or without double quotes preceding and/or trailing
*/
export const HaymakerEmptyAdTitlePatterns = Object.freeze([
  /#HAYMAKER_EMPTY_TOKEN#/i,
  /HM_PARSE_\d+,"?\w+"?,"?\w+"?,EMPTY/i,
]);

export const PlaybackConstants = Object.freeze({
  AUTO_PLAY_ENFORCE_SECS: 15,
  AUTO_PLAY_JUMP_RATIO: 0.5,
  IMA_TIME_UPDATE_INTERVAL_MS: 200,
  MIME_TYPES: ['webm', 'ogg', 'mp4'],
  DEFAULT_MIME_TYPE: 'video/mp4',
  TIMEOUT_BUFFER_MS: 10000,
  IMA_ADS_COMPLETE_DELAY_MS: 1500,
  IMA_MAX_RELOAD_ATTEMPTS: 8,
  DEFAULT_VIDEO_START_TIMEOUT_MS: 30000,
});

// cspell:disable
export const PlaybackValues = Object.freeze({
  SUCCESS: 'success',
  FAILURE: 'failure',
  VPAID: 'vpaid',
  VAST: 'vast',
  NONE: 'none',
  ERROR: 'error',
  UNKNOWN: 'unknown',
  TRACKER: 'tracker',
  IMPRESSION: 'impression',
});

export const Renderers = Object.freeze({
  IMASDK: 'ima_sdk',
  PANDORA: 'pandora',
});
// cspell: enable

export const VastValues = Object.freeze({
  AD_SYSTEM: 'Pandora Ads',
  AD_TITLE: 'Pandora Wrapped Vast Ad',
  COMPANION_DIMENSIONS: Object.freeze({
    NORMAL: Object.freeze({
      WIDTH: 300,
      HEIGHT: 250,
      NAME: 'medium_rectangle',
    }),
    DOUBLE: Object.freeze({
      WIDTH: 300,
      HEIGHT: 600,
      NAME: 'double_wide',
    }),
  }),
});

export const Dimensions = Object.freeze([
  { width: 1152, height: 648 },
  { width: 1024, height: 576 },
  { width: 768, height: 432 },
  { width: 583, height: 328 },
]);

export const FlexModalTypes = Object.freeze({
  BACKGROUNDED_RESUME: 'BACKGROUNDED_RESUME',
  DISMISS_RESUME: 'DISMISS_RESUME',
});

export const FlexMessages = defineMessages({
  flexReward1: {
    id: 't.flexReward1',
    defaultMessage: '{action} {offerName} in',
  },
  flexReward2: {
    id: 't.flexReward2',
    defaultMessage: 'Get',
  },
  flexReward2b: {
    id: 't.flexReward2',
    defaultMessage: 'Play',
  },
  flexReward3: {
    id: 't.flexReward3',
    defaultMessage: 'Thank you for your time',
  },
  flexReward4: {
    id: 't.flexReward4',
    defaultMessage: 'Now enjoy your {offerName}!',
  },
  flexReward5: {
    id: 't.flexReward5',
    defaultMessage: 'Get My {offerName}',
  },
  flexReward6: {
    id: 't.flexReward6',
    defaultMessage: 'Are you sure?',
  },
  flexReward7: {
    id: 't.flexReward7',
    defaultMessage: "Just a few more seconds and you'll unlock your {rewardType}.",
  },
  flexReward8: {
    id: 't.flexReward8',
    defaultMessage: 'Leave This Ad',
  },
  flexReward9: {
    id: 't.flexReward9',
    defaultMessage: 'Resume',
  },
  flexRewardPremiumAccess: {
    id: 't.flexRewardPremiumAccess',
    defaultMessage: 'Premium Access',
  },
  flexPARewardCTA: {
    id: 't.flexPARewardCTA',
    defaultMessage: 'Start My Reward',
  },
  flexRewardYourMusic: {
    id: 't.flexRewardYourMusic',
    defaultMessage: 'your music',
  },
  flexResumeVideoTitle: {
    id: 't.flexResumeVideoTitle',
    defaultMessage: 'Resume Your Video',
  },
  flexResumeVideoBody: {
    id: 't.flexResumeVideoBody',
    defaultMessage: 'Resume your video to enjoy your {rewardType}',
  },
  flexResumeVideoCta: {
    id: 't.flexResumeVideoTitleCta',
    defaultMessage: 'Resume',
  },
  flexResumeVideoDismiss: {
    id: 't.flexResumeVideoDismiss',
    defaultMessage: 'No thanks',
  },
  flexLearnMore: {
    id: 't.flexLearnMore',
    defaultMessage: 'Learn More',
  },
  flexLearnMoreAboutOffer: {
    id: 't.flexLearnMoreAboutOffer',
    defaultMessage: 'Learn more about this offer',
  },
});

export const EventTypes = Object.freeze({
  LEARN_MORE: 'learn_more',
});

export const IAS = Object.freeze({
  AD_NETWORK_ID: '927641',
  AD_UNIT_ID: 'NOW_PLAYING',
});

export const VideoAnalyticsKeys = Object.freeze([
  'action',
  'ad_type',
  'ad_sub_type',
  'creative_id',
  'correlation_id',
  'client_correlation_id',
  'duration',
  'has_scrubbed',
  'line_id',
  'name',
  'offer_name',
  'parse_method',
  'playback_pos',
  'progress_enforced',
  'reason',
  'renderer',
  'station_id',
  'status',
  'tracking_event_type',
  'trigger_action',
  'user_timed_out',
  'url',
]);

// cspell:disable
export const VideoLifecycleActions = Object.freeze({
  VAD_LIFECYCLE_GET_AD_LIST: 'vad_lifecycle_get_ad_list',
  VAD_LIFECYCLE_INIT_PLAYER: 'vad_lifecycle_init_player',
  VAD_LIFECYCLE_VIDEO_ERROR: 'vad_lifecycle_video_error',
  VAD_LIFECYCLE_TRACKING_EVENT: 'vad_lifecycle_tracking_event',
  VAD_LIFECYCLE_VIDEO_END: 'vad_lifecycle_video_end',
  VAD_LIFECYCLE_VIDEO_DFP_IMPRESSION: 'vad_lifecycle_video_dfp_impression',
  VAD_LIFECYCLE_REGISTER_VIDEO_AD_IMPRESSION: 'vad_lifecycle_register_video_impression',
  VAD_LIFECYCLE_FETCH_DFP: 'vad_lifecycle_fetch_dfp',
  VAD_LIFECYCLE_FETCH_DIRECT_SOLD: 'vad_lifecycle_fetch_direct_sold',
  VAD_LIFECYCLE_FETCH_VAST: 'vad_lifecycle_fetch_vast',
  VAD_LIFECYCLE_FETCH_VPAID: 'vad_lifecycle_fetch_vpaid',
  VAD_LIFECYCLE_GENERATE_VAST: 'vad_lifecycle_generate_vast',
  VAD_LIFECYCLE_PARSE_DFP: 'vad_lifecycle_parse_dfp',
  VAD_LIFECYCLE_PARSE_VAST: 'vad_lifecycle_parse_vast',
  VAD_LIFECYCLE_PARSE_VPAID: 'vad_lifecycle_parse_vpaid',
  VAD_LIFECYCLE_PARSE_VPAID_SOURCES: 'vad_lifecycle_parse_vpaid_sources',
  VAD_LIFECYCLE_LOAD_GOOGLE_IMA_SDK: 'vad_lifecycle_load_google_ima_sdk',
  VAD_LIFECYCLE_IAS_IMA_ADAPTER_FAIL: 'vad_lifecycle_ias_ima_adapter_fail',
});
// cspell: enable

export const TapToVideoEvents = Object.freeze({
  // used in the event_type column of event_tap_to_video
  TAP_TO_VIDEO_INITIATE: 'TAP_TO_VIDEO_INITIATE',
  TAP_TO_VIDEO_START: 'TAP_TO_VIDEO_START',
  TAP_TO_VIDEO_FIRST_QUARTILE: 'TAP_TO_VIDEO_FIRST_QUARTILE',
  TAP_TO_VIDEO_SECOND_QUARTILE: 'TAP_TO_VIDEO_SECOND_QUARTILE',
  TAP_TO_VIDEO_THIRD_QUARTILE: 'TAP_TO_VIDEO_THIRD_QUARTILE',
  TAP_TO_VIDEO_COMPLETE: 'TAP_TO_VIDEO_COMPLETE',
  TAP_TO_VIDEO_SKIP: 'TAP_TO_VIDEO_SKIP',
  TAP_TO_VIDEO_BACKGROUND: 'TAP_TO_VIDEO_BACKGROUND',
  TAP_TO_VIDEO_FOREGROUND: 'TAP_TO_VIDEO_FOREGROUND',
  TAP_TO_VIDEO_PAUSE: 'TAP_TO_VIDEO_PAUSE',
  TAP_TO_VIDEO_RESUME_COACHMARK_DISPLAYED: 'TAP_TO_VIDEO_RESUME_COACHMARK_DISPLAYED',
  TAP_TO_VIDEO_RESUME_FROM_COACHMARK: 'TAP_TO_VIDEO_RESUME_FROM_COACHMARK',
  TAP_TO_VIDEO_SKIP_FROM_COACHMARK: 'TAP_TO_VIDEO_SKIP_FROM_COACHMARK',
  TAP_TO_VIDEO_RESUME_BACKGROUND_COACHMARK_DISPLAYED:
    'TAP_TO_VIDEO_RESUME_BACKGROUND_COACHMARK_DISPLAYED',
  TAP_TO_VIDEO_RESUME_FROM_BACKGROUND_COACHMARK: 'TAP_TO_VIDEO_RESUME_FROM_BACKGROUND_COACHMARK',
  TAP_TO_VIDEO_SKIP_FROM_BACKGROUND_COACHMARK: 'TAP_TO_VIDEO_SKIP_FROM_BACKGROUND_COACHMARK',
  TAP_TO_VIDEO_CLICKED: 'TAP_TO_VIDEO_CLICKED',
  TAP_TO_VIDEO_ERROR: 'TAP_TO_VIDEO_ERROR',
});

export const TapToVideoEventNameMap = Object.freeze({
  [TapToVideoEvents.TAP_TO_VIDEO_INITIATE]: 'initiate',
  [TapToVideoEvents.TAP_TO_VIDEO_START]: 'start',
  [TapToVideoEvents.TAP_TO_VIDEO_FIRST_QUARTILE]: 'firstQuartile',
  [TapToVideoEvents.TAP_TO_VIDEO_SECOND_QUARTILE]: 'secondQuartile',
  [TapToVideoEvents.TAP_TO_VIDEO_THIRD_QUARTILE]: 'thirdQuartile',
  [TapToVideoEvents.TAP_TO_VIDEO_COMPLETE]: 'complete',
  [TapToVideoEvents.TAP_TO_VIDEO_SKIP]: 'skip',
  [TapToVideoEvents.TAP_TO_VIDEO_BACKGROUND]: 'background',
  [TapToVideoEvents.TAP_TO_VIDEO_FOREGROUND]: 'foreground',
  [TapToVideoEvents.TAP_TO_VIDEO_PAUSE]: 'pause',
  [TapToVideoEvents.TAP_TO_VIDEO_RESUME_COACHMARK_DISPLAYED]: 'resume_coachmark_displayed',
  [TapToVideoEvents.TAP_TO_VIDEO_RESUME_FROM_COACHMARK]: 'resume_from_coachmark',
  [TapToVideoEvents.TAP_TO_VIDEO_SKIP_FROM_COACHMARK]: 'skip_from_coachmark',
  [TapToVideoEvents.TAP_TO_VIDEO_RESUME_BACKGROUND_COACHMARK_DISPLAYED]:
    'resume_background_coachmark_displayed',
  [TapToVideoEvents.TAP_TO_VIDEO_RESUME_FROM_BACKGROUND_COACHMARK]:
    'resume_from_background_coachmark',
  [TapToVideoEvents.TAP_TO_VIDEO_SKIP_FROM_BACKGROUND_COACHMARK]: 'skip_from_background_coachmark',
  [TapToVideoEvents.TAP_TO_VIDEO_CLICKED]: 'clicked',
  [TapToVideoEvents.TAP_TO_VIDEO_ERROR]: 'error',
});
export const TapToVideoEventsValues = Object.keys(TapToVideoEventNameMap);

export const TapToVideoKeys = Object.freeze([
  'creative_id',
  'line_id',
  'duration',
  'event_type',
  'station_id',
  'playable_source_id',
  'offer_name',
  'progress_enforced',
]);

// cspell:disable-next-line
export const moatPartnerCode = 'pandoraimavideo354813165505';
